/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Mantine
import {
	Image,
	Box,
	Drawer as NativeDrawer,
	Text,
	Button,
	Tooltip,
	Stack,
} from '@mantine/core';

// Style
import classes from './DrawerConversionFactorShowMore.module.css';

// Utils
import { truncateString } from '@utils/functions';

// Icons
import ProductionLogo from '@assets/images/production-logo.svg';
import { IconEdit, IconTrash } from '@tabler/icons-react';

// I18n
import { useTranslation } from 'react-i18next';

// Type
import { TParsedConversionFactor } from '../../types';
import { VerifiedButton } from '@components/atoms/VerifiedButton';
import clsx from 'clsx';
import { useAuthorization } from '@lib/authorization';
import { ACLS } from '@config/constants/acls';

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */

type DrawerConversionFactorShowMoreProps = {
	onClose: () => void;
	handledelete?: () => void;
	opened: boolean;
	data: Partial<TParsedConversionFactor>;
	position: 'right' | 'left';
	fromstepper?: boolean;
};
/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const DrawerConversionFactorShowMore = (
	props: DrawerConversionFactorShowMoreProps
) => {
	const {
		opened,
		onClose,
		handledelete,
		fromstepper,
		data: conversionFactor,
		...restProps
	} = props;

	const {
		organization,
		entity,
		project,
		site,
		ratio,
		startDate,
		isUsed,
		note,
	} = conversionFactor;
	/* ********************************  HOOKS  ********************************* */

	const { t } = useTranslation();
	const { checkAcl } = useAuthorization();
	/* ******************************** RENDERING ******************************* */
	if (conversionFactor)
		return (
			<NativeDrawer
				onClose={onClose}
				opened={opened}
				classNames={classes}
				overlayProps={{
					backgroundOpacity: 0.2,
					blur: 0.5,
				}}
				{...restProps}
			>
				{/* Header */}
				<Box className={classes.customHeader}>
					<Box className={classes.containerLogo}>
						<Image
							className={classes.logo}
							src={ProductionLogo}
						/>
					</Box>

					<Box className={classes.containerTitle}>
						<Text className={classes.customSubTitle}>
							{t('global.project')}
						</Text>
						{props.data.project && (
							<Text className={classes.customTitle}>
								{props.data.project}
							</Text>
						)}
					</Box>
				</Box>

				{/* Body */}
				<Box className={classes.bodyDrawer}>
					{organization && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.organization')}
							</Text>
							<Tooltip
								label={organization}
								disabled={organization.length <= 20}
							>
								<Text className={classes.value}>
									{truncateString(organization, 20)}
								</Text>
							</Tooltip>
						</Box>
					)}
					{entity && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.producer')}
							</Text>
							<Tooltip
								label={entity}
								disabled={entity.length <= 20}
							>
								<Text className={classes.value}>
									{truncateString(entity, 20)}
								</Text>
							</Tooltip>
						</Box>
					)}
					{project && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.project')}
							</Text>
							<Tooltip
								label={project}
								disabled={project.length <= 20}
							>
								<Text className={classes.value}>
									{truncateString(project, 20)}
								</Text>
							</Tooltip>
						</Box>
					)}

					{site && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.site')}
							</Text>
							<Tooltip
								label={site}
								disabled={site.length <= 20}
							>
								<Text className={classes.value}>
									{truncateString(site, 20)}
								</Text>
							</Tooltip>
						</Box>
					)}

					{ratio && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.ratio')}
							</Text>
							<Tooltip
								label={ratio}
								disabled={ratio.length <= 20}
							>
								<Text className={classes.value}>
									{truncateString(ratio, 20)}
								</Text>
							</Tooltip>
						</Box>
					)}

					{startDate && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.startDate')}
							</Text>
							<Tooltip
								label={startDate}
								disabled={startDate.length <= 20}
							>
								<Text className={classes.value}>
									{truncateString(startDate, 20)}
								</Text>
							</Tooltip>
						</Box>
					)}

					<Box className={classes.row}>
						<Text className={classes.label}>
							{t('global.status')}
						</Text>
						<Tooltip
							label={
								isUsed
									? t('conversionFactors.usedOnMint')
									: t('conversionFactors.notUsedOnMint')
							}
						>
							<Text className={classes.value}>
								<VerifiedButton
									data-testid="P2DlDXR7_Z5jdHi6B-button"
									readOnly={fromstepper}
									isverified={!!isUsed}
								>
									{isUsed
										? t('global.used')
										: t('global.notUsed')}
								</VerifiedButton>
							</Text>
						</Tooltip>
					</Box>
					{note && (
						<Box className={classes.rowNote}>
							<Text className={classes.label}>
								{t('global.note')}
							</Text>
							<Tooltip
								label={note}
								disabled={note.length <= 80}
							>
								<Text
									className={clsx(
										classes.value,
										classes.valueNote
									)}
								>
									{truncateString(note, 80)}
								</Text>
							</Tooltip>
						</Box>
					)}
				</Box>

				{/* Footer */}
				{!fromstepper &&
					checkAcl(ACLS.CONVERSION_FACTORS.DELETE) && (
						<Stack
							gap={'10px'}
							className={classes.footer}
						>
							<Button
								disabled={props.data.isUsed}
								onClick={handledelete}
								leftSection={<IconTrash size={16} />}
								hiddenFrom="xxl"
								size="sm"
								fullWidth
								classNames={{
									root: classes.customDeleteButton,
								}}
							>
								{t(
									'conversionFactors.deleteConversionFactor'
								)}
							</Button>
							<Button
								disabled={props.data.isUsed}
								onClick={handledelete}
								leftSection={<IconTrash size={20} />}
								size="md"
								visibleFrom="xxl"
								fullWidth
								classNames={{
									root: classes.customDeleteButton,
								}}
							>
								{t(
									'conversionFactors.deleteConversionFactor'
								)}
							</Button>
						</Stack>
					)}
			</NativeDrawer>
		);
};
